'use client';
import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Info, CheckCircle, AlertCircle } from 'lucide-react';

export interface ModalDialogProps {
	isOpen: boolean;
	title: string;
	message: string;
	onConfirm: () => void;
	onCancel?: () => void; // Optional cancel
	type?: 'danger' | 'warning' | 'info' | 'success';
	confirmText?: string;
	cancelText?: string;
}

const icons = {
	danger: <AlertCircle className="w-6 h-6" />,
	warning: <AlertTriangle className="w-6 h-6" />,
	info: <Info className="w-6 h-6" />,
	success: <CheckCircle className="w-6 h-6" />,
};

const colors = {
	danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
	warning: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
	info: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
	success: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
};

const iconColors = {
	danger: 'bg-red-100 text-red-600',
	warning: 'bg-amber-100 text-amber-600',
	info: 'bg-blue-100 text-blue-600',
	success: 'bg-green-100 text-green-600',
};

export const ModalDialog: React.FC<ModalDialogProps> = ({
	isOpen,
	title,
	message,
	onConfirm,
	onCancel,
	type = 'info',
	confirmText = 'OK',
	cancelText = 'Cancel',
}) => {
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				className="absolute inset-0 bg-black/40 backdrop-blur-sm"
				onClick={onCancel || onConfirm}
			/>
			<motion.div
				initial={{ opacity: 0, scale: 0.95, y: 10 }}
				animate={{ opacity: 1, scale: 1, y: 0 }}
				exit={{ opacity: 0, scale: 0.95, y: 10 }}
				className="bg-white dark:bg-zinc-900 rounded-xl shadow-xl max-w-sm w-full relative overflow-hidden"
			>
				<div className="p-6">
					<div className="flex items-center gap-4 mb-4">
						<div className={`p-3 rounded-full ${iconColors[type]}`}>
							{icons[type]}
						</div>
						<h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
							{title}
						</h3>
					</div>
					<p className="text-zinc-600 dark:text-zinc-400 mb-6 whitespace-pre-wrap">
						{message}
					</p>
					<div className="flex justify-end gap-3">
						{onCancel && (
							<button
								onClick={onCancel}
								className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
							>
								{cancelText}
							</button>
						)}
						<button
							onClick={onConfirm}
							className={`px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition-colors ${colors[type]}`}
						>
							{confirmText}
						</button>
					</div>
				</div>
			</motion.div>
		</div>
	);
};
